// ─────────────────────────────────────────────────────────────────────────────
// Generate a runnable host RPC server for whatever schema a widget declares.
//
// Every widget that wants host data needs the same server: routes that return
// its record values as JSON. That is entirely derivable from the schema, so the
// Designer writes it rather than making each author work out the shape, the
// field widths, or the CORS headers.
//
// The generated server contains no protocol knowledge — no bit packing, no RPC
// ids, no begin/commit sequencing. It returns numbers; the Designer and the
// widget runtime do the rest.
// ─────────────────────────────────────────────────────────────────────────────

import { fieldOffsets, type SnapshotSchema } from "../data/schemas";

/** Human-readable range for a field, so generated comments explain the limits. */
export function fieldRange(bits: number, signed: boolean | undefined): string {
  return signed ? `${-(2 ** (bits - 1))}..${2 ** (bits - 1) - 1}` : `0..${2 ** bits - 1}`;
}

/** A starter value that is always inside the declared width. */
function seedValue(bits: number, signed: boolean | undefined, index: number): number {
  const max = signed ? 2 ** (bits - 1) - 1 : 2 ** bits - 1;
  return Math.min(index + 1, max);
}

/** The exact JSON body a server must return for this schema. */
export function serverResponseShape(schema: SnapshotSchema): Record<string, Record<string, number>> {
  const values: Record<string, Record<string, number>> = {};
  for (const [recordName, record] of Object.entries(schema.records)) {
    values[recordName] = {};
    Object.entries(record.fields).forEach(([field, spec], index) => {
      values[recordName][field] = seedValue(spec.bits, spec.signed, index);
    });
  }
  return values;
}

function identifier(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9]+/gu, "-").replace(/^-+|-+$/gu, "").toLowerCase();
  return cleaned || "widget";
}

/**
 * Emit a complete, dependency-free Node server for one widget's schemas.
 *
 * `schemas` is the widget's own hostData map, so the routes, field names,
 * widths and ranges all come from what that widget actually declared.
 */
export function generateHostServer(
  widgetName: string,
  schemas: Record<string, SnapshotSchema>,
  port = 842,
): string {
  const slug = identifier(widgetName);
  const names = Object.keys(schemas);
  if (names.length === 0) {
    throw new Error("This widget declares no host-data schemas, so it needs no server.");
  }

  const readers = names.map((name) => {
    const schema = schemas[name];
    const lines: string[] = [];
    lines.push(`function read${name.charAt(0).toUpperCase()}${name.slice(1)}() {`);
    lines.push(`  // Replace these with your real values. Every field is an integer,`);
    lines.push(`  // and the Designer rejects anything outside the declared range.`);
    lines.push(`  return {`);
    lines.push(`    values: {`);
    for (const [recordName, record] of Object.entries(schema.records)) {
      const offsets = fieldOffsets(record);
      lines.push(`      // ${recordName} -> host.rpc 0x${record.id.toString(16).toUpperCase()}`);
      lines.push(`      ${recordName}: {`);
      Object.entries(record.fields).forEach(([field, spec], index) => {
        const range = fieldRange(spec.bits, spec.signed);
        const labels = spec.labels ? `  labels: ${spec.labels.join(", ")}` : "";
        lines.push(
          `        ${field}: ${seedValue(spec.bits, spec.signed, index)},` +
            ` // ${spec.bits} bits @${offsets[field]}, ${range}${labels}`,
        );
      });
      lines.push(`      },`);
    }
    lines.push(`    },`);
    lines.push(`    note: "${slug} host " + new Date().toLocaleTimeString(),`);
    lines.push(`  };`);
    lines.push(`}`);
    return lines.join("\n");
  });

  const routes = names
    .map((name) => `  "/${identifier(name)}": read${name.charAt(0).toUpperCase()}${name.slice(1)},`)
    .join("\n");

  return `#!/usr/bin/env node
// Host RPC server for "${widgetName}".
// Generated by the F1 Widget Designer from the widget's own host-data schema.
//
//   node ${slug}-host.mjs            # http://localhost:${port}
//   node ${slug}-host.mjs --port 9000
//
// Then in the Designer: Host data -> paste the URL -> Fetch. The Designer packs
// these numbers into the widget's RPC events, so this file needs no protocol
// knowledge: no bit packing, no RPC ids, no begin/commit sequencing.

import { createServer } from "node:http";

const args = process.argv.slice(2);
const portFlag = args.indexOf("--port");
const PORT = portFlag >= 0 ? Number(args[portFlag + 1]) : ${port};

${readers.join("\n\n")}

const ROUTES = {
${routes}
};

const server = createServer((request, response) => {
  const url = new URL(request.url, \`http://localhost:\${PORT}\`);
  // The Designer runs on a different origin, so CORS is required.
  response.setHeader("access-control-allow-origin", "*");
  response.setHeader("access-control-allow-headers", "content-type");
  if (request.method === "OPTIONS") { response.statusCode = 204; response.end(); return; }

  const handler = ROUTES[url.pathname];
  if (!handler) {
    response.statusCode = 404;
    response.setHeader("content-type", "application/json");
    response.end(JSON.stringify({ error: \`No route \${url.pathname}. Try \${Object.keys(ROUTES).join(", ")}.\` }));
    return;
  }
  try {
    const body = JSON.stringify(handler(url));
    response.setHeader("content-type", "application/json");
    response.end(body);
    console.log(\`\${request.method} \${url.pathname} -> \${body.length} bytes\`);
  } catch (cause) {
    response.statusCode = 500;
    response.end(JSON.stringify({ error: String(cause?.message ?? cause) }));
  }
});

server.listen(PORT, () => {
  console.log(\`${widgetName} host on http://localhost:\${PORT}\`);
  for (const route of Object.keys(ROUTES)) console.log(\`  http://localhost:\${PORT}\${route}\`);
});
`;
}

/** Default endpoint the Designer suggests for a schema. */
export function defaultEndpoint(name: string, port = 842): string {
  return `http://localhost:${port}/${identifier(name)}`;
}
